import { StandardEvents } from '@shopify/events';

/**
 * Keeps the preorder notice in step with the selected size.
 *
 * The variant picker refetches the section on every size change but the
 * product form only morphs the add to cart button, so anything else in the
 * form keeps showing the first variant. This takes its own copy out of the
 * refetched HTML - the Liquid stays the single place the notice is written.
 * The data-preorder flag it carries is what hides Buy it now.
 */
class PreorderSlot extends HTMLElement {
  connectedCallback() {
    this.target = this.closest('.shopify-section, dialog, product-card');
    this.target?.addEventListener(StandardEvents.productSelect, this.#onSelect);
    this.#skipPreorderLanding();
    this.#handBuyNowToGokwik();
  }

  /**
   * A preorder variant counts as available, so Shopify lands the page on it
   * (usually XS). When the customer did not ask for a variant, move to the
   * nearest in-stock one - changing the last option first, so the colour
   * they clicked through on is kept and only the size moves.
   */
  #skipPreorderLanding() {
    if (!this.hasAttribute('data-preorder')) return;
    if (new URL(window.location.href).searchParams.has('variant')) return;

    const preorder = new Set((this.dataset.preorderVariants || '').split(',').filter(Boolean));
    const radios = [...(this.target?.querySelectorAll('input[type="radio"][data-variant-id]') || [])];
    const byFieldset = new Map();
    for (const r of radios) {
      const key = r.dataset.fieldsetIndex;
      if (!byFieldset.has(key)) byFieldset.set(key, []);
      byFieldset.get(key).push(r);
    }

    // Size first, whichever position it holds, so the colour is kept.
    const groups = [...byFieldset.values()].reverse();
    groups.sort((a, b) => (/size/i.test(b[0].dataset.optionName) ? 1 : 0) - (/size/i.test(a[0].dataset.optionName) ? 1 : 0));

    for (const group of groups) {
      const pick = group.find(
        (r) => !r.checked && r.dataset.optionAvailable === 'true' && !preorder.has(r.dataset.variantId)
      );
      if (pick) {
        pick.click();
        return;
      }
    }
  }

  disconnectedCallback() {
    this.target?.removeEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  #onSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.newProduct) this.dataset.productId = detail.newProduct.id;
        else if (detail.productId && detail.productId !== this.dataset.productId) return;

        const next = detail.html.querySelector(`preorder-slot[data-product-id="${this.dataset.productId}"]`)
          || detail.html.querySelector('preorder-slot');
        if (!next) return;
        this.innerHTML = next.innerHTML;
        this.toggleAttribute('data-preorder', next.hasAttribute('data-preorder'));
        if (!this.hasAttribute('data-preorder')) requestAnimationFrame(() => this.#handBuyNowToGokwik());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[preorder-slot]', error);
      });
  };

  /**
   * GoKwik only takes over a Buy it now button that is visible when it first
   * sees it, and after that only notices buttons newly added to the page. A
   * page that opened on a preorder size had Buy it now hidden at that moment,
   * so it stayed Shopify's plain button. Re-inserting it once it is visible
   * counts as newly added, and GoKwik claims it as normal.
   */
  #handBuyNowToGokwik(attempt = 0) {
    // Retried because either side may not be ready yet: GoKwik's script
    // loads deferred, and Shopify draws the button itself after page load.
    // Keeps checking for ~15s after load or a size change, and does nothing
    // while the size is a preorder (the button is meant to be hidden then).
    if (attempt > 30 || !this.isConnected) return;
    const scope = this.closest('.product-form-buttons');
    const button = scope?.querySelector('.accelerated-checkout-block button');
    if (button?.dataset.gokwikProcessed) return;
    if (button?.offsetParent && !this.hasAttribute('data-preorder')) button.parentNode.appendChild(button);
    setTimeout(() => this.#handBuyNowToGokwik(attempt + 1), 500);
  }
}

if (!customElements.get('preorder-slot')) customElements.define('preorder-slot', PreorderSlot);
